import styled from 'styled-components';
import classNames from 'classnames';

import {useHistory} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {Award as LucideAward, Check, ChevronRight, Eraser, Hand, Handshake} from "lucide-react";
import {useLoggedIn, useRelativeTime, useStoreComment, useStoreProfile} from '../store';
import usePress from "../utils/use_press";
import useModal from "../modals";
import {themecolors} from "../utils/themecolors";
import useProfilePicture from "../utils/profile_picture";
import NavLink from './nav_link';
import RoleIndicator from "./role_indicator";
import Skeleton from "./skeleton";
import TextContent from "./text_content";
import {BareButton} from "./button";
import SeparatorBullet from "./separator_bullet";
import ProfilePicture from "./profile_picture";
import CommunityLogo from "./community_logo";
import {CommentTextInputBox} from "./create_comment";
import Sheet from "./bottom-sheet/sheet";
import SignupPromptSheet from "./bottom-sheet/signup_prompt";
import {useSheetModal} from "./test/ModalContext";

const Info = styled.div``;
const Author = styled.div``;
const Settings = styled.div``;
const Age = styled.div``;
const Score = styled.div``;
const Details = styled.div``;
const PostTitle = styled.div``;
const Main = styled.div``;
const Collapsed = styled.div``;
const Deleted = styled.span``;
const CollapsedPreview = styled.div``;
const Options = styled.div``;
const Edited = styled.span``;
const NewUser = styled.span``;
const Approved = styled.span``;
const Award = styled.span``;
const Deeper = styled.div``;

const Avatar = styled.div``;

const Bar = styled.div``;
const InnerBar = styled.div``;
const OffshootBar = styled.div``;
const CenteredIndent = styled.div``;

const PostIn = styled.span``;
const PostCommunity = styled.span``;

const CommunityLogoWrapper = styled.div``;

export const Indent = styled.div``;

export const Comment = styled(({className, post, comment, prevDepth=0, depth=0, nextDepth=0, divider,
                                   Actions, collapsed, setCollapsed, queueContext, noContext = false, goDeeper,
                               indentMap = [], setIndentMap, commentMap}) => {

    const {theme} = useStoreProfile();
    const {author, created, content, post_title, post_uuid, post_created} = comment || {};
    const ago = useRelativeTime(created, false);
    const modal = useModal();
    const pressProps = usePress(
        /* onShort: */ () => setCollapsed && setCollapsed(comment.id, false),
        /* onLong: */ () => setCollapsed && setCollapsed(comment.id, !collapsed),
    );

    // Get child comments from commentMap
    const childComments = commentMap ? commentMap.get(comment?.id) || [] : [];
    
    // Determine if this comment is the last child of its parent
    const parentComment = useStoreComment(comment?.in_reply_to_id || 0);
    const parentChildIds = parentComment ? parentComment.child_ids : [];
    // const isLastChild = parentChildIds.length > 0 && parentChildIds[parentChildIds.length - 1] === comment?.id;

    const renderIndentation = () => {
        if (!comment) return [];
        
        const indentElements = [];
        
        for (let i = 0; i < depth; i++) {
            // For each level of indentation, determine if we should show a vertical line
            const isLastLevel = i === (depth - 1);
            
            // Show vertical line if:
            // 1. Not the last level of indentation OR
            // 2. It is the last level but this comment is not the last child of its parent
            const shouldShowVerticalLine = !isLastLevel || (isLastLevel);
            
            indentElements.push(
                <Indent key={i}>
                    {shouldShowVerticalLine && <InnerBar />}
                    {isLastLevel && <OffshootBar />}
                </Indent>
            );
        }
        
        return indentElements;
    };
    
    const indentElements = renderIndentation();

    const handleCollapse = () => {
        if (setCollapsed && comment) {
            setCollapsed(comment.id, !collapsed);
        }
    };

    const reported = comment?.moderation && comment?.moderation?.is_reported;
    const removed = comment?.moderation && comment?.moderation?.removed_at !== 0;

    const history = useHistory();
    const handleDeeper = (postId, commentId) => {
        if (postId && commentId) {
            history.push(`/p/${postId}/x/c/${commentId}/`);
        }
    };

    const [reply, setReply] = useState(false);
    const loggedIn = useLoggedIn();
    const { openModal } = useSheetModal();

    const handleReply = () => {
        if (!loggedIn) {
            const modalContent = ({ handleCloseModal }) => (
                <Sheet onClose={() => handleCloseModal() } padding={"header"}>
                    <SignupPromptSheet onClose={() => handleCloseModal() } />
                </Sheet>
            );
            openModal(modalContent);
        } else {
            setReply(!reply);
        }
    };

    // don't't render anything if comment is undefined
    if (!comment) {
        return <div />;
    }

    return (
        <>
            <div className={classNames(className, `d_${prevDepth}_${depth}_${nextDepth}`, { divider, }, {removed, reported: reported && !queueContext})}>
                {indentElements}
                {noContext &&
                    <Main style={{
                        overflow: 'initial',
                        flexGrow: 0,
                        paddingRight: '8px',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Avatar><ProfilePicture size={20} fontSize={11} username={comment.author} profilePicture={useProfilePicture(comment.author, comment.profile_picture, theme)} /></Avatar>
                        <Bar>
                            {!collapsed && !post_title && childComments.length > 0 &&
                            <InnerBar/>
                            }
                        </Bar>
                    </Main>
                }
                <Main>
                    {comment ? (
                        post_created && !noContext && (
                            <>
                                <PostTitle>
                                    <CommunityLogoWrapper>
                                        <CommunityLogo name={comment?.community}/>
                                    </CommunityLogoWrapper>
                                    <NavLink href={`/p/${post_uuid}`}>
                                        {post_title ? post_title : '[removed]'}
                                    </NavLink>
                                </PostTitle>
                            </>
                        )
                    ) : (
                        <Skeleton height={12}/>
                    )}
                    {comment ? (
                        <Info onClick={handleCollapse}>
                            {author ? (
                                <Author>
                                    <NavLink href={`/u/${author}`}>
                                        {author}
                                    </NavLink>
                                    <RoleIndicator
                                        admin={comment?.is_admin}
                                        mod={comment?.is_moderator}
                                        op={post?.author && comment?.author && comment.author === post.author}
                                        proTier={comment?.pro_tier}
                                    />
                                </Author>
                            ) : (
                                <Deleted>deleted</Deleted>
                            )}
                            <SeparatorBullet />
                            <Age>
                                <NavLink href={`/p/${post_uuid || post?.uuid}/x/c/${comment.uuid}/`}>
                                    {ago} ago
                                </NavLink>
                            </Age>
                            {comment?.awards > 0 &&
                                <Award>
                                    <SeparatorBullet />
                                    <LucideAward size={14} color={"#dcba00"}/>
                                </Award>
                            }
                            {comment?.is_edited && (
                                <Edited>
                                    <Eraser size={14} />
                                </Edited>
                            )}
                            {comment?.is_new_user && (
                                <NewUser>
                                    <Hand size={14} />
                                </NewUser>
                            )}
                            {comment?.moderation && comment?.moderation?.approved_at !== 0 && <Approved><Check size={16} /></Approved>}
                        </Info>
                    ) : (
                        <Skeleton height={12} width={120} />
                    )}
                    {!comment && <Skeleton height={12} count={1} />}
                    {!collapsed && <TextContent content={content} type="Comment" removalSource={comment?.removal_source} />}
                    {!collapsed && Actions && (
                        <Actions
                            handleReply={handleReply}
                            comment={comment}
                            collapsed={collapsed}
                            setCollapsed={setCollapsed}
                            isTopLevel={depth === 0}
                        />
                    )}
                    {goDeeper && comment &&
                        <Deeper>
                            <BareButton onClick={() => handleDeeper(comment.parent_uuid, comment.uuid)}>
                                Load more replies
                                <ChevronRight size={14} />
                            </BareButton>
                        </Deeper>
                    }
                    {reply && <CommentTextInputBox comment={comment} onClose={() => setReply(false)} />}
                </Main>
            </div>
            
            {/* Render child comments if not collapsed */}
            {!collapsed && childComments.length > 0 && childComments.map((childComment, index) => (
                <Comment
                    key={childComment.id}
                    post={post}
                    comment={childComment}
                    prevDepth={depth}
                    depth={depth + 1}
                    nextDepth={index < childComments.length - 1 ? depth + 1 : depth}
                    Actions={Actions}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    queueContext={queueContext}
                    noContext={noContext}
                    indentMap={indentMap}
                    setIndentMap={setIndentMap}
                    commentMap={commentMap}
                />
            ))}
        </>
    );
})`
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_1 : theme.white_0};
  padding: 0 16px;
  font-size: 15px;
  display: flex;
  flex-direction: row;

  ${InnerBar} {
    width: 1px;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.lighter_border};
    height: 100%;
  }
  
  ${Bar} {
    flex-shrink: 0;
    width: 21px;
    margin: 8px auto 0 auto;
    display: flex;
    flex-grow: 1;
    justify-content: center;
  }
  
  &.divider {
    border-bottom: 0px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_1 : themecolors.light_border};
    padding-bottom: 6px;
  }
  
  ${OffshootBar} {
    position: absolute;
    width: 12px;
    height: 17px;
    margin-left: 11px;
    border-left: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.lighter_border};
    border-bottom: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.lighter_border};
    border-bottom-left-radius: 8px;
  }
  ${Indent} {
    flex-shrink: 0;
    width: 21px;
    display: flex;
    position: relative;
    justify-content: center;
    margin-right: 2px;
  }
  ${Main} {
    padding-top: ${({depth}) => depth === 0 ? '12px' : '4px'};
    flex-grow: 1;
    overflow: hidden;
  }

  ${({noContext}) => !noContext ? `
    ${Main} {
       padding-top: 12px;
       padding-bottom: 8px;
    }
  ` : ``}
  
  ${({collapsed}) => collapsed ? `
    ${Main} {
       padding-bottom: 10px;
    }
  ` : ``}
  
  ${Info} {
    display: flex;
    font-size: 13px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_medium_impact_text};
    align-items: center;
    padding: 2px 0;
    white-space: nowrap;
    overflow: hidden;
  }

  ${Settings} {
    margin-left: auto;
  }

  ${Author}, ${Deleted} {
    display: flex;
    font-weight: 600;
  }
  ${Author} {
    display: flex;
    color: ${({theme}) => theme.color.name === 'dark' ? theme.white_0 : themecolors.light_high_impact_text};
    align-items: center;
    white-space: nowrap;
    font-size: 13px;
  }
  ${Author} a {
    padding-right: 2px;
  }

  ${Settings} {
    color: #a7a7a7;
  }

  ${Details} {
    margin-left: auto;
    display: flex;
  }
  ${Score} {
    font-weight: 500;
    margin-right: 8px;
  }
  ${PostTitle} {
    font-weight: 600;
    font-size: 13px;
    line-height: 23px;
    padding-top: 2px;
    padding-bottom: 6px;
    display: flex;
  }
  ${PostTitle} a {
    overflow: hidden;
    display: -webkit-box;
    //noinspection CssUnknownProperty
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
  ${TextContent} {
    margin-top: 2px;
    font-size: 14px;
    margin-bottom: 2px;
  }
  ${CollapsedPreview} {
    height: 14px;
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  ${CollapsedPreview} p {
    display: none;
  }
  ${CollapsedPreview} p:first-of-type {
    display: initial;
    font-size: 14px;
    overflow: hidden;
    white-space: nowrap;
    margin-left: 12px;
    text-overflow: ellipsis;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
  }
  ${Options} {
    margin-left: auto;
  }
  ${NewUser}, ${Edited} {
    display: flex;
    font-size: 11px;
    margin-left: 8px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
  }
  ${Approved} {
    color: ${({theme}) => themecolors.green};
    margin: 0 8px;
    font-size: 13px;
  }
  ${Award} {
    display: flex;
    align-items: center;
    white-space: break-spaces;
  }
  ${Deeper} {
    padding: 2px 0px 0px 0px;
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 8px;
  }
  ${Deeper} button {
    display: flex;
    align-items: center;
    width: 100%;
    white-space: nowrap;
    text-align: left;
    font-weight: 400;
    color:  ${() => themecolors.link};
  }
  ${Deeper} button svg {
    margin-left: 8px;
  }
  &.removed {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.pink_2 : theme.pink_1};
  }
  &.reported {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.yellow_2 : theme.yellow_1};
  }
  ${Avatar} {
    width: 20px;
    margin-top: 0px;
  }
  ${Avatar} img {
    border-radius: 50%;
  }
  ${PostIn} {
    font-weight: 400;
    font-size: 13px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  ${PostCommunity} {
    font-weight: 400;
    font-size: 13px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }

  ${CommunityLogoWrapper} {
    height: 24px;
    width: 24px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  ${CommunityLogoWrapper} img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
  
`;

export default Comment;
